import type { AiNotes } from '../core/ai-notes';
import { clipKey, type Clip, type FileRef, type Footage } from '../core/footage';
import type { EventInfo } from '../core/reasons';
import type { ClipScan } from '../core/scan';
import type { Telemetry } from '../core/telemetry';
import { buildTimeline, type Timeline } from '../core/timeline';
import { buildTriage, eventEntry, type EventEntry, type PlacedMoment, type TriageItem } from '../core/triage';

export interface LibraryFile extends FileRef {
  readonly file: Blob;
}

export type LibraryClip = Clip<LibraryFile>;
export type LibraryFootage = Footage<LibraryFile>;

export interface Progress {
  readonly done: number;
  readonly total: number;
}

export class Library {
  private readonly infos = new Map<string, EventInfo | undefined>();
  private readonly notes = new Map<string, AiNotes>();
  private readonly results = new Map<string, ClipScan | Error>();
  private readonly hidden = new Set<string>();

  constructor(private readonly all: readonly LibraryFootage[]) {}

  get footage(): readonly LibraryFootage[] {
    return this.all.filter((item) => !this.hidden.has(item.id));
  }

  hide(footage: LibraryFootage): void {
    this.hidden.add(footage.id);
  }

  restore(footage: LibraryFootage): void {
    this.hidden.delete(footage.id);
  }

  setInfo(footageId: string, info: EventInfo | undefined): void {
    this.infos.set(footageId, info);
  }

  infoOf(footage: LibraryFootage): EventInfo | undefined {
    return this.infos.get(footage.id);
  }

  setNotes(footageId: string, notes: AiNotes | undefined): void {
    if (notes) this.notes.set(footageId, notes);
    else this.notes.delete(footageId);
  }

  notesOf(footage: LibraryFootage): AiNotes | undefined {
    return this.notes.get(footage.id);
  }

  setResult(clip: LibraryClip, result: ClipScan | Error): void {
    this.results.set(clipKey(clip), result);
  }

  scanOf(clip: LibraryClip): ClipScan | undefined {
    const result = this.results.get(clipKey(clip));
    return result instanceof Error ? undefined : result;
  }

  failureOf(clip: LibraryClip): string | undefined {
    const result = this.results.get(clipKey(clip));
    return result instanceof Error ? result.message : undefined;
  }

  settled(clip: LibraryClip): boolean {
    return this.results.has(clipKey(clip));
  }

  footageById(id: string): LibraryFootage | undefined {
    return this.footage.find((item) => item.id === id);
  }

  entry(footage: LibraryFootage): EventEntry<LibraryFile> {
    return eventEntry(footage, this.infoOf(footage), this.momentsOf(footage), this.notesOf(footage));
  }

  items(): readonly TriageItem<LibraryFile>[] {
    return buildTriage(this.footage.map((item) => this.entry(item)));
  }

  frontTelemetry(footage: LibraryFootage, slot: string): Telemetry | undefined {
    const front = footage.clips.find((clip) => clip.slot === slot && clip.camera === 'front');
    return front && this.scanOf(front)?.telemetry;
  }

  timeline(footage: LibraryFootage): Timeline<LibraryFile> | undefined {
    if (!footage.clips.every((clip) => this.settled(clip))) return undefined;
    return buildTimeline({
      clips: footage.clips,
      header: (clip) => this.scanOf(clip),
      frontTelemetry: (slot) => this.frontTelemetry(footage, slot),
    });
  }

  frontProgress(): Progress {
    const fronts = new Map(this.footage.flatMap((item) => item.clips).filter((clip) => clip.camera === 'front').map((clip) => [clipKey(clip), clip]));
    return { done: [...fronts.values()].filter((clip) => this.settled(clip)).length, total: fronts.size };
  }

  private momentsOf(footage: LibraryFootage): readonly PlacedMoment[] {
    return footage.clips.flatMap((clip) => {
      const scan = clip.camera === 'front' ? this.scanOf(clip) : undefined;
      if (scan?.createdAt === undefined) return [];
      const createdAt = scan.createdAt;
      return scan.moments.map((moment) => ({ ...moment, at: createdAt + moment.time * 1000 }));
    });
  }
}
